import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import { canOpenPortalJob } from '../../utils/portalJobNavigation';
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Table, Spinner, Alert, Button, Form, Col, Row, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useDispatch, useSelector } from "react-redux";
import { usePermissions } from "../../hooks/usePermissions";
import { fetchData, postData, patchData } from "../../redux/slices/apiCallSlice";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import PdfViewerModal from "../common/PdfViewerModal";
import axiosInstance from "../../axios/axiosConfig";
import { getProjectId, getProjectStatus, idsMatch } from "../../utils/dataNormalization";
import { PRODUCTION_SCHEDULE_PATH } from "../../config/productionRoutes";

const NP_SCHEDULE_HDPE_FIELDS = [
  "HDPEDueDate",
  "HDPEOutSourced",
  "HDPEOrdered",
  "HDPEStatus",
  "HDPEDetails",
  "HDPEDeliveryStatus",
  "HDPEReviewStatus",
];

const NP_SCHEDULE_HDPE_TABLE_FIELD_ORDER = [
  "HDPEDueDate",
  "HDPEDetails",
  "HDPEStatus",
  "HDPEReviewStatus",
  "HDPEOutSourced",
  "HDPEOrdered",
  "HDPEDeliveryStatus",
];

const NP_SCHEDULE_ATTACHMENT_FIELD = "SharePointFileIds";
const NP_SCHEDULE_ATTACHMENT_ID_DELIMITER = "|";
const NP_SCHEDULE_ATTACHMENT_MAX_COUNT = 2;
const NP_SHAREPOINT_ATTACHMENTS_ROOT_FOLDER = "NPAttachments";
const NP_SHAREPOINT_ATTACHMENTS_ENTITY_FOLDER = "npSchedule";

const NP_HDPE_STATUS_FIELDS = new Set([
  "HDPEStatus",
  "HDPEDeliveryStatus",
  "HDPEReviewStatus",
]);

const NP_HDPE_BOOLEAN_FIELDS = new Set([
  "HDPEOutSourced",
  "HDPEOrdered",
]);

const NP_SCHEDULE_STATUS_OPTIONS = [
  "Upcoming",
  "Not Started",
  "Pending",
  "In Progress",
  "Completed",
  "On Hold",
  "Ordered",
  "Shipped",
];

const DATE_PLACEHOLDER_VALUES = new Set([
  "",
  "na",
  "n/a",
  "none",
  "null",
  "undefined",
  "tbd",
  "-",
  "--",
]);

const CATEGORY_DUE_DATE_FIELD_MAP = {
  ezFertigations: "NPFertigationDue",
  bmx: "NPBMXDue",
  horizontalInducerTubes: "NPInducerTubeDue",
  rockets: "NPInducerTubeDue",
  hdpeSuctionLines: "NPSuctionLineDue",
};

const STATUS_PILL_THEME = {
  neutral: {
    backgroundColor: "#eceff3",
    borderColor: "#c9d1db",
    color: "#475467",
  },
  info: {
    backgroundColor: "#dff7f6",
    borderColor: "#9fd9d6",
    color: "#0f6b6b",
  },
  progress: {
    backgroundColor: "#dceefe",
    borderColor: "#9dc5ea",
    color: "#1d5f91",
  },
  upcoming: {
    backgroundColor: "#ece6ff",
    borderColor: "#c7b6ff",
    color: "#5b3da6",
  },
  warning: {
    backgroundColor: "#fff2cf",
    borderColor: "#efcf83",
    color: "#8b5e00",
  },
  success: {
    backgroundColor: "#dff3e4",
    borderColor: "#a8d5b5",
    color: "#216e39",
  },
  danger: {
    backgroundColor: "#fde7e9",
    borderColor: "#efb3ba",
    color: "#a1262c",
  },
};

const STATUS_PILL_BASE_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "1.75rem",
  padding: "0.15rem 0.65rem",
  borderRadius: "999px",
  borderStyle: "solid",
  borderWidth: "1px",
  fontSize: "0.78rem",
  fontWeight: 600,
  lineHeight: 1.2,
  whiteSpace: "normal",
  textAlign: "center",
};

const SECTION_THEME = {
  hdpe: {
    headerBg: "#dceefe",
    cellBg: "#f5fbff",
    border: "#74a9d8",
    accent: "#2d6fa3",
  },
};

const TABLE_SCROLL_CONTAINER_STYLE = {
  position: "relative",
  maxHeight: "72vh",
  overflowX: "auto",
  overflowY: "auto",
};

const STICKY_EXPAND_COL_WIDTH = 48;
const STICKY_JOB_NUMBER_COL_WIDTH = 130;
const STICKY_PROJECT_NAME_COL_WIDTH = 280;

const STICKY_COLUMN_LEFT = {
  expand: 0,
  jobNumber: STICKY_EXPAND_COL_WIDTH,
  projectName: STICKY_EXPAND_COL_WIDTH + STICKY_JOB_NUMBER_COL_WIDTH,
};

const STICKY_HEADER_STYLE = {
  position: "sticky",
  top: 0,
  backgroundColor: "#f8f9fa",
  zIndex: 9,
};

const getStickyHeaderColumnStyle = (columnKey, extraStyle = {}) => ({
  ...STICKY_HEADER_STYLE,
  left: STICKY_COLUMN_LEFT[columnKey] ?? 0,
  zIndex: 11,
  ...extraStyle,
});

const getStickyBodyColumnStyle = (columnKey, extraStyle = {}) => ({
  position: "sticky",
  left: STICKY_COLUMN_LEFT[columnKey] ?? 0,
  backgroundColor: "#fff",
  zIndex: 4,
  ...extraStyle,
});

const normalizeSearchValue = (value) => String(value ?? "").trim().toLowerCase();

const includesAnyStatusToken = (normalizedValue, tokens = []) => (
  tokens.some((token) => normalizedValue.includes(token))
);

const getStatusPillTheme = (value) => {
  const normalizedValue = normalizeSearchValue(value);

  if (!normalizedValue) {
    return STATUS_PILL_THEME.neutral;
  }

  if (includesAnyStatusToken(normalizedValue, ["not started", "not-started"])) {
    return STATUS_PILL_THEME.danger;
  }

  if (includesAnyStatusToken(normalizedValue, ["cancel", "reject", "issue", "overdue", "late", "failed"])) {
    return STATUS_PILL_THEME.danger;
  }

  if (includesAnyStatusToken(normalizedValue, ["complete", "completed", "done", "shipped", "approved", "closed"])) {
    return STATUS_PILL_THEME.success;
  }

  if (includesAnyStatusToken(normalizedValue, ["in progress", "progress", "active", "started", "running"])) {
    return STATUS_PILL_THEME.progress;
  }

  if (includesAnyStatusToken(normalizedValue, ["upcoming", "ready", "scheduled"])) {
    return STATUS_PILL_THEME.upcoming;
  }

  if (includesAnyStatusToken(normalizedValue, ["ordered", "order", "preflight", "review"])) {
    return STATUS_PILL_THEME.info;
  }

  if (includesAnyStatusToken(normalizedValue, ["on hold", "hold", "blocked", "waiting", "pending"])) {
    return STATUS_PILL_THEME.warning;
  }

  return STATUS_PILL_THEME.neutral;
};

const renderStatusPill = (value) => {
  const displayValue = String(value ?? "").trim();

  if (!displayValue) {
    return "";
  }

  const theme = getStatusPillTheme(displayValue);

  return (
    <span
      title={displayValue}
      style={{
        ...STATUS_PILL_BASE_STYLE,
        backgroundColor: theme.backgroundColor,
        borderColor: theme.borderColor,
        color: theme.color,
      }}
    >
      {displayValue}
    </span>
  );
};

const getHdpeBoundaryStyle = (fieldName) => {
  const theme = SECTION_THEME.hdpe;
  const firstField = NP_SCHEDULE_HDPE_FIELDS[0];
  const lastField = NP_SCHEDULE_HDPE_FIELDS[NP_SCHEDULE_HDPE_FIELDS.length - 1];

  return {
    backgroundColor: theme.cellBg,
    ...(fieldName === firstField ? { borderLeft: `3px solid ${theme.border}` } : {}),
    ...(fieldName === lastField ? { borderRight: `3px solid ${theme.border}` } : {}),
  };
};

const getHdpeHeaderCellStyle = (fieldName) => {
  const theme = SECTION_THEME.hdpe;

  return {
    ...getHdpeBoundaryStyle(fieldName),
    backgroundColor: theme.headerBg,
    color: theme.accent,
  };
};

const getNpScheduleProjectId = (record) => (
  record?.ProjectID ??
  record?.projectID ??
  record?.ProjectId ??
  record?.projectId ??
  null
);

const getNpScheduleAttachmentIdsValue = (record) => (
  record?.[NP_SCHEDULE_ATTACHMENT_FIELD] ??
  record?.sharePointFileIds ??
  null
);

const parseSharePointFileIds = (value) => {
  const uniqueIds = new Set(
    String(value ?? "")
      .split(NP_SCHEDULE_ATTACHMENT_ID_DELIMITER)
      .map((entry) => entry.trim())
      .filter(Boolean)
  );

  return Array.from(uniqueIds);
};

const stringifySharePointFileIds = (ids = []) => {
  const normalizedIds = Array.from(new Set((Array.isArray(ids) ? ids : [])
    .map((entry) => String(entry ?? "").trim())
    .filter(Boolean)));

  return normalizedIds.length > 0
    ? normalizedIds.join(NP_SCHEDULE_ATTACHMENT_ID_DELIMITER)
    : null;
};

const sanitizeAttachmentFileName = (fileName) => String(fileName ?? "")
  .trim()
  .replace(/[<>:"/\\|?*]+/g, "-")
  .replace(/\s+/g, " ");

const encodeGraphPathSegment = (value) => encodeURIComponent(String(value ?? "").trim());

const formatAttachmentFileSize = (bytes) => {
  if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) {
    return "-";
  }

  const size = Number(bytes);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getGraphItemDisplayUrl = (item) => (
  item?.webUrl || item?.["@microsoft.graph.downloadUrl"] || ""
);

const isViewableAttachmentFile = (fileName) => {
  const extension = String(fileName ?? "").split(".").pop()?.toLowerCase();
  return extension === "pdf";
};

const isPlaceholderDateValue = (value) => DATE_PLACEHOLDER_VALUES.has(String(value ?? "").trim().toLowerCase());

const canAttemptDateParsing = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (isPlaceholderDateValue(trimmedValue)) {
      return false;
    }

    return !Number.isNaN(Date.parse(trimmedValue));
  }

  const parsedDate = new Date(value);
  return !Number.isNaN(parsedDate.getTime());
};

const findNpScheduleMatch = (project, scheduleRows = []) => {
  const projectId = getProjectId(project);

  const projectIdMatch = scheduleRows.find((record) =>
    idsMatch(getNpScheduleProjectId(record), projectId)
  );

  if (projectIdMatch) {
    return { record: projectIdMatch, matchType: "projectId" };
  }

  const jobNumber = normalizeSearchValue(project?.JobNumber);
  if (jobNumber) {
    const jobNumberMatch = scheduleRows.find((record) =>
      normalizeSearchValue(record?.JobNumber) === jobNumber
    );

    if (jobNumberMatch) {
      return { record: jobNumberMatch, matchType: "jobNumber" };
    }
  }

  const projectName = normalizeSearchValue(project?.ProjectName);
  if (projectName) {
    const projectNameMatch = scheduleRows.find((record) =>
      normalizeSearchValue(record?.ProjectName) === projectName
    );

    if (projectNameMatch) {
      return { record: projectNameMatch, matchType: "projectName" };
    }
  }

  return { record: null, matchType: null };
};

const toPayloadProjectId = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const trimmedValue = String(value).trim();

  if (trimmedValue === "") {
    return null;
  }

  if (/^-?\d+$/.test(trimmedValue)) {
    return Number(trimmedValue);
  }

  return trimmedValue;
};

const NPJobs = ({ apiData = {}, loading = false, error = null }) => {
  const { instance, accounts } = useMsal();
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const { canView } = usePermissions();
  const navigate = useNavigate();
  const jobClickTimeoutRef = useRef(null);

  const [categorizedData, setCategorizedData] = useState({});
  const [npScheduleRows, setNpScheduleRows] = useState([]);
  const [npScheduleError, setNpScheduleError] = useState(null);
  const [filters, setFilters] = useState({
    hideShippedJobs: true,
  });
  const [sortConfig, setSortConfig] = useState({
    ezFertigations: { key: "JobNumber", direction: "ascending" },
    bmx: { key: "JobNumber", direction: "ascending" },
    horizontalInducerTubes: { key: "JobNumber", direction: "ascending" },
    rockets: { key: "JobNumber", direction: "ascending" },
    hdpeSuctionLines: { key: "JobNumber", direction: "ascending" },
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedScheduleJob, setSelectedScheduleJob] = useState(null);
  const [modalFormData, setModalFormData] = useState({});
  const [isSavingModal, setIsSavingModal] = useState(false);
  useRealtimeDraft(showScheduleModal || isSavingModal);
  const [modalError, setModalError] = useState("");

  const [attachmentItems, setAttachmentItems] = useState([]);
  const [selectedAttachmentFiles, setSelectedAttachmentFiles] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [deletingAttachmentIds, setDeletingAttachmentIds] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");
  const [attachmentMessage, setAttachmentMessage] = useState("");
  const [attachmentActionRowId, setAttachmentActionRowId] = useState(null);
  const [showAttachmentViewer, setShowAttachmentViewer] = useState(false);
  const [loadingAttachmentViewer, setLoadingAttachmentViewer] = useState(false);
  const [attachmentViewerUrl, setAttachmentViewerUrl] = useState("");
  const [attachmentViewerExternalUrl, setAttachmentViewerExternalUrl] = useState("");
  const [attachmentViewerTitle, setAttachmentViewerTitle] = useState("");
  const [showAttachmentPicker, setShowAttachmentPicker] = useState(false);
  const [attachmentPickerItems, setAttachmentPickerItems] = useState([]);
  const [attachmentPickerJobLabel, setAttachmentPickerJobLabel] = useState("");

  const npSharePointSiteId = process.env.REACT_APP_SHAREPOINT_NP_SITE_ID;
  const npSharePointDriveId = process.env.REACT_APP_SHAREPOINT_NP_DRIVE_ID;
  const hasNpSharePointConfig = Boolean(npSharePointSiteId && npSharePointDriveId);

  const categories = [
    { key: "ezFertigations", title: "EZ Fertigations" },
    { key: "bmx", title: "BMX Models" },
    { key: "horizontalInducerTubes", title: "Horizontal Inducer Tubes" },
    { key: "rockets", title: "Vertical Inducer Tubes (Rockets)" },
    { key: "hdpeSuctionLines", title: "HDPE Suction Lines" },
  ];

  useEffect(() => {
    return () => {
      if (jobClickTimeoutRef.current) {
        clearTimeout(jobClickTimeoutRef.current);
      }
    };
  }, []);

  const reloadNpScheduleRows = useCallback(async () => {
    if (!apiUrls?.npSchedule) {
      setNpScheduleRows([]);
      return;
    }

    setNpScheduleError(null);

    try {
      const responseData = await dispatch(fetchData({ url: apiUrls.npSchedule })).unwrap();
      setNpScheduleRows(Array.isArray(responseData) ? responseData : []);
    } catch (loadError) {
      setNpScheduleError(loadError?.message || "Failed to load NP Schedule rows.");
      setNpScheduleRows([]);
    }
  }, [apiUrls?.npSchedule, dispatch]);

  useEffect(() => {
    if (Array.isArray(apiData?.npSchedule)) {
      setNpScheduleRows(apiData.npSchedule);
      setNpScheduleError(null);
      return;
    }

    reloadNpScheduleRows();
  }, [apiData?.npSchedule, reloadNpScheduleRows]);

  useEffect(() => {
    if (!apiData || Object.keys(apiData).length === 0) {
      setCategorizedData({});
      return;
    }

    const npJobsData = {
      ezFertigations: [],
      bmx: [],
      horizontalInducerTubes: [],
      rockets: [],
      hdpeSuctionLines: [],
    };

    const scheduleRows = Array.isArray(apiData?.npSchedule) && apiData.npSchedule.length > 0
      ? apiData.npSchedule
      : npScheduleRows;

    const enrichWithJobDetails = (items, sourceTable = "generalProject") => {
      if (!items || !apiData.generalProject) return items;

      return items
        .map((item) => {
          const projectId = sourceTable === "generalProject" ? item.ID : getProjectId(item);
          const projectDetails = apiData.generalProject.find(
            (project) => idsMatch(getProjectId(project), projectId)
          );

          const fallbackProject = projectDetails || item;
          const { record: npScheduleRecord, matchType } = findNpScheduleMatch(fallbackProject, scheduleRows);

          return {
            ...item,
            ...(projectDetails || {}),
            ...(npScheduleRecord || {}),
            JobNumber: npScheduleRecord?.JobNumber || projectDetails?.JobNumber || item?.JobNumber || "N/A",
            ProjectName: projectDetails?.ProjectName || item?.ProjectName || "N/A",
            ModelNumber: projectDetails?.ModelNumber || item?.ModelNumber || "N/A",
            ProjectStatus: projectDetails?.ProjectStatus || projectDetails?.JobStatus || item?.ProjectStatus || "N/A",
            ProjectID: getProjectId(projectDetails) ?? getProjectId(item),
            ID: item.ID,
            NpScheduleRecordID: npScheduleRecord?.ID ?? null,
            NpScheduleHasMatch: Boolean(npScheduleRecord),
            NpScheduleMatchType: matchType,
          };
        })
        .filter((item) => getProjectStatus(item) !== "Shipped");
    };

    if (apiData.fertigation && apiData.generalProject) {
      const fertigationItems = apiData.fertigation.filter((item) =>
        item.HasFertigation === true &&
        item.WatertronicsFertigation &&
        item.WatertronicsFertigation.startsWith("EZ")
      );

      npJobsData.ezFertigations = enrichWithJobDetails(fertigationItems, "fertigation")
        .filter((item) => item.JobNumber && item.JobNumber !== "N/A");
    }

    if (apiData.generalProject) {
      const bmxItems = apiData.generalProject.filter((item) =>
        item.ModelNumber && item.ModelNumber.startsWith("BMX")
      );
      npJobsData.bmx = enrichWithJobDetails(bmxItems, "generalProject");
    }

    if (apiData.pumpManifoldPiping) {
      const horizontalInducerItems = apiData.pumpManifoldPiping.filter((item) =>
        item.HasPiping === true &&
        item.HDPEAccessoryForSubPump === "Horizontal Inducer Tube"
      );
      npJobsData.horizontalInducerTubes = enrichWithJobDetails(horizontalInducerItems, "pumpManifoldPiping");

      const rocketItems = apiData.pumpManifoldPiping.filter((item) =>
        item.HasPiping === true &&
        item.HDPEAccessoryForSubPump === "Vertical Inducer Tube (Rocket)"
      );
      npJobsData.rockets = enrichWithJobDetails(rocketItems, "pumpManifoldPiping");
    }

    if (apiData.hcRequirements) {
      const hdpeItems = apiData.hcRequirements.filter((item) =>
        item.SuctionLineMaterial === "HDPE" &&
        item.SuctionLinesBy === "Watertronics"
      );
      npJobsData.hdpeSuctionLines = enrichWithJobDetails(hdpeItems, "hcRequirements");
    }

    setCategorizedData(npJobsData);
  }, [apiData, npScheduleRows]);

  const acquireGraphAccessToken = useCallback(async () => {
    const account = accounts?.[0];

    if (!instance || !account) {
      throw new Error("Unable to access your Microsoft account session.");
    }

    const tokenResponse = await instance.acquireTokenSilent({
      scopes: ["https://graph.microsoft.com/.default"],
      account,
    });

    return tokenResponse.accessToken;
  }, [accounts, instance]);

  const getAttachmentFolderSegments = useCallback((job) => {
    const recordId = String(job?.NpScheduleRecordID ?? "").trim();

    return [
      NP_SHAREPOINT_ATTACHMENTS_ROOT_FOLDER,
      NP_SHAREPOINT_ATTACHMENTS_ENTITY_FOLDER,
      recordId,
    ].filter(Boolean);
  }, []);

  const fetchGraphFolderChildren = useCallback(async (folderId, accessToken) => {
    const response = await axiosInstance.get(
      `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${folderId}/children?$top=200`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    return Array.isArray(response.data?.value) ? response.data.value : [];
  }, [npSharePointDriveId, npSharePointSiteId]);

  const ensureAttachmentFolderId = useCallback(async (job, accessToken) => {
    const folderSegments = getAttachmentFolderSegments(job);
    let currentFolderId = "root";

    for (const folderName of folderSegments) {
      const children = await fetchGraphFolderChildren(currentFolderId, accessToken);
      const existingFolder = children.find((item) => item.folder && item.name === folderName);

      if (existingFolder) {
        currentFolderId = existingFolder.id;
        continue;
      }

      const createResponse = await axiosInstance.post(
        `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${currentFolderId}/children`,
        {
          name: folderName,
          folder: {},
          "@microsoft.graph.conflictBehavior": "fail",
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      currentFolderId = createResponse.data?.id;
    }

    return currentFolderId;
  }, [fetchGraphFolderChildren, getAttachmentFolderSegments, npSharePointDriveId, npSharePointSiteId]);

  const fetchGraphItemById = useCallback(async (itemId, accessToken) => {
    const response = await axiosInstance.get(
      `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${itemId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  }, [npSharePointDriveId, npSharePointSiteId]);

  const persistAttachmentIds = useCallback(async (job, nextIds) => {
    if (!job?.NpScheduleRecordID) {
      throw new Error("Save the NP Schedule record before managing attachments.");
    }

    const nextValue = stringifySharePointFileIds(nextIds);

    await dispatch(patchData({
      url: apiUrls.npSchedule,
      data: {
        ID: job.NpScheduleRecordID,
        ProjectId: toPayloadProjectId(job.ProjectID ?? job.NpScheduleProjectID),
        [NP_SCHEDULE_ATTACHMENT_FIELD]: nextValue,
      },
      allowFullPayload: true,
      contract: {
        requestCase: "pascal",
        renameMap: {
          projectId: "ProjectId",
        },
      },
    })).unwrap();

    setSelectedScheduleJob((previousJob) => {
      if (!previousJob) {
        return previousJob;
      }

      return {
        ...previousJob,
        [NP_SCHEDULE_ATTACHMENT_FIELD]: nextValue,
      };
    });

    await reloadNpScheduleRows();
  }, [apiUrls.npSchedule, dispatch, reloadNpScheduleRows]);

  useEffect(() => {
    const loadAttachments = async () => {
      if (!showScheduleModal || !selectedScheduleJob) {
        return;
      }

      setAttachmentError("");
      setAttachmentMessage("");
      setSelectedAttachmentFiles([]);

      const fileIds = parseSharePointFileIds(getNpScheduleAttachmentIdsValue(selectedScheduleJob));

      if (!hasNpSharePointConfig || fileIds.length === 0) {
        setAttachmentItems([]);
        setLoadingAttachments(false);
        return;
      }

      setLoadingAttachments(true);

      try {
        const accessToken = await acquireGraphAccessToken();
        const items = await Promise.all(fileIds.map(async (fileId) => {
          try {
            return await fetchGraphItemById(fileId, accessToken);
          } catch (readError) {
            return {
              id: fileId,
              name: `Unavailable file (${fileId})`,
              unavailable: true,
              error: readError?.message || "Failed to load attachment metadata.",
            };
          }
        }));

        setAttachmentItems(items);
      } catch (loadError) {
        console.error("Failed to load NP attachments:", loadError);
        setAttachmentItems([]);
        setAttachmentError(loadError?.message || "Failed to load attachments.");
      } finally {
        setLoadingAttachments(false);
      }
    };

    loadAttachments();
  }, [acquireGraphAccessToken, fetchGraphItemById, hasNpSharePointConfig, selectedScheduleJob, showScheduleModal]);

  useEffect(() => {
    return () => {
      if (attachmentViewerUrl && attachmentViewerUrl.startsWith("blob:")) {
        URL.revokeObjectURL(attachmentViewerUrl);
      }
    };
  }, [attachmentViewerUrl]);

  const handleFilterChange = (e) => {
    const { name, checked } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: checked,
    }));
  };

  const handleSort = (category, key) => {
    setSortConfig((prevConfig) => {
      const currentConfig = prevConfig[category];
      let direction = "ascending";
      if (currentConfig.key === key && currentConfig.direction === "ascending") {
        direction = "descending";
      }

      return {
        ...prevConfig,
        [category]: { key, direction },
      };
    });
  };

  const getItemsByCategory = (category) => categorizedData[category] || [];

  const formatDate = (dateString) => {
    if (!canAttemptDateParsing(dateString)) return "";
    const parsedDate = new Date(dateString);
    if (Number.isNaN(parsedDate.getTime())) return "";
    const options = { year: "numeric", month: "long", day: "numeric" };
    return parsedDate.toLocaleDateString(undefined, options);
  };

  const getEffectiveDueDate = (job, category) => {
    // If np-schedule has an override, use it
    if (job?.HDPEDueDate && String(job.HDPEDueDate).trim() !== "") {
      return job.HDPEDueDate;
    }
    // Otherwise fall back to the category's corresponding general-project field
    const generalProjectField = CATEGORY_DUE_DATE_FIELD_MAP[category];
    if (generalProjectField && job?.[generalProjectField] && String(job[generalProjectField]).trim() !== "") {
      return job[generalProjectField];
    }
    return null;
  };

  const formatHdpeFieldLabel = (fieldName, { includePrefix = true } = {}) => {
    const label = fieldName.replace(/^HDPE/, "");
    const formattedLabel = label.replace(/([A-Z])/g, " $1").trim();
    return includePrefix ? `HDPE ${formattedLabel}` : formattedLabel;
  };

  const formatModalValue = (fieldName, rawValue) => {
    if (NP_HDPE_BOOLEAN_FIELDS.has(fieldName)) {
      if (rawValue === true || String(rawValue).toLowerCase() === "true") return "true";
      if (rawValue === false || String(rawValue).toLowerCase() === "false") return "false";
      return "";
    }

    if (fieldName.endsWith("Date")) {
      if (!canAttemptDateParsing(rawValue)) {
        return "";
      }

      const parsedDate = new Date(rawValue);
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      return `${parsedDate.getFullYear()}-${month}-${day}`;
    }

    return rawValue ?? "";
  };

  const getModalValueForSave = (fieldName, rawValue) => {
    if (NP_HDPE_BOOLEAN_FIELDS.has(fieldName)) {
      if (rawValue === "true") return true;
      if (rawValue === "false") return false;
      return null;
    }

    if (fieldName.endsWith("Date")) {
      const trimmedValue = String(rawValue ?? "").trim();
      return trimmedValue ? trimmedValue : null;
    }

    const trimmedValue = String(rawValue ?? "").trim();
    return trimmedValue === "" ? null : trimmedValue;
  };

  const getBooleanToggleDisplayLabel = (value) => {
    if (value === "true") return "Yes";
    if (value === "false") return "No";
    return "Not set";
  };

  const renderCellValue = (item, fieldName, category) => {
    let value = item?.[fieldName];

    // For HDPEDueDate, use the effective due date (override or fallback to general-project)
    if (fieldName === "HDPEDueDate") {
      value = getEffectiveDueDate(item, category);
      return formatDate(value) || "N/A";
    }

    if (NP_HDPE_STATUS_FIELDS.has(fieldName)) {
      return renderStatusPill(value) || "N/A";
    }

    if (NP_HDPE_BOOLEAN_FIELDS.has(fieldName)) {
      if (value === true || String(value).toLowerCase() === "true") return "Yes";
      if (value === false || String(value).toLowerCase() === "false") return "No";
      return "N/A";
    }

    return value === null || value === undefined || value === "" ? "N/A" : String(value);
  };

  const filterAndSortItems = (items, category) => {
    if (!items || items.length === 0) return [];

    const filtered = items.filter((item) => {
      const projectStatus = getProjectStatus(item);
      const hasJobStatus = String(item?.JobStatus ?? "").trim() !== "";

      const hideShippedFilter = filters.hideShippedJobs
        ? projectStatus !== "Shipped" && projectStatus !== "canceled"
        : true;

      const jobStatusFilter = ["ezFertigations", "horizontalInducerTubes", "rockets", "hdpeSuctionLines"].includes(category)
        ? hasJobStatus
        : true;

      const searchFilter = searchTerm
        ? item.JobNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.ProjectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.HDPEDetails?.toLowerCase().includes(searchTerm.toLowerCase())
        : true;

      return hideShippedFilter && jobStatusFilter && searchFilter;
    });

    const config = sortConfig[category];

    return [...filtered].sort((a, b) => {
      const key = config.key;
      const direction = config.direction;

      if (key === "JobNumber") {
        const aValue = parseFloat(a[key]) || 0;
        const bValue = parseFloat(b[key]) || 0;
        return direction === "ascending" ? aValue - bValue : bValue - aValue;
      }

      if (key === "BaseLength" || key === "BaseWidth") {
        const aValue = parseFloat(a[key]) || 0;
        const bValue = parseFloat(b[key]) || 0;
        return direction === "ascending" ? aValue - bValue : bValue - aValue;
      }

      if (key === "HDPEDueDate" || key === "PackDate") {
        const aDate = a[key] ? new Date(a[key]) : null;
        const bDate = b[key] ? new Date(b[key]) : null;

        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;

        if (Number.isNaN(aDate.getTime()) && Number.isNaN(bDate.getTime())) return 0;
        if (Number.isNaN(aDate.getTime())) return 1;
        if (Number.isNaN(bDate.getTime())) return -1;

        return direction === "ascending" ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
      }

      const aValue = String(a[key] ?? "").toLowerCase();
      const bValue = String(b[key] ?? "").toLowerCase();

      if (aValue < bValue) {
        return direction === "ascending" ? -1 : 1;
      }

      if (aValue > bValue) {
        return direction === "ascending" ? 1 : -1;
      }

      return 0;
    });
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prevRows) => (
      prevRows.includes(rowId)
        ? prevRows.filter((id) => id !== rowId)
        : [...prevRows, rowId]
    ));
  };

  const handleOpenJob = (job) => {
    if (!canOpenPortalJob(job)) return;
    const jobId = job.ProjectID || job.ID;
    navigate(`/${jobId}/generalInfo`);
  };

  const handleJobNumberClick = (job) => {
    if (jobClickTimeoutRef.current) {
      clearTimeout(jobClickTimeoutRef.current);
    }

    jobClickTimeoutRef.current = setTimeout(() => {
      handleOpenJob(job);
      jobClickTimeoutRef.current = null;
    }, 220);
  };

  const handleOpenScheduleModal = (job, category) => {
    const initialFormData = NP_SCHEDULE_HDPE_FIELDS.reduce((accumulator, fieldName) => {
      // For HDPEDueDate, use the effective due date (override or fallback to general-project)
      const value = fieldName === "HDPEDueDate" 
        ? getEffectiveDueDate(job, category)
        : job?.[fieldName];
      accumulator[fieldName] = formatModalValue(fieldName, value);
      return accumulator;
    }, {});

    setSelectedScheduleJob(job);
    setModalFormData(initialFormData);
    setModalError("");
    setShowScheduleModal(true);
  };

  const handleJobNumberDoubleClick = (event, job, category) => {
    event.stopPropagation();

    if (jobClickTimeoutRef.current) {
      clearTimeout(jobClickTimeoutRef.current);
      jobClickTimeoutRef.current = null;
    }

    handleOpenScheduleModal(job, category);
  };

  const handleCloseScheduleModal = (forceClose = false) => {
    if (isSavingModal && !forceClose) {
      return;
    }

    setShowScheduleModal(false);
    setSelectedScheduleJob(null);
    setModalFormData({});
    setModalError("");
    setAttachmentItems([]);
    setSelectedAttachmentFiles([]);
    setAttachmentError("");
    setAttachmentMessage("");
  };

  const handleModalFieldChange = (fieldName, nextValue) => {
    setModalFormData((previousValues) => ({
      ...previousValues,
      [fieldName]: nextValue,
    }));
  };

  const handleSaveScheduleModal = async () => {
    if (!selectedScheduleJob) {
      return;
    }

    setIsSavingModal(true);
    setModalError("");

    const payload = {
      ...(selectedScheduleJob.NpScheduleRecordID ? { ID: selectedScheduleJob.NpScheduleRecordID } : {}),
      ProjectId: toPayloadProjectId(selectedScheduleJob.ProjectID),
      JobNumber: selectedScheduleJob.JobNumber ?? null,
      ProjectName: selectedScheduleJob.ProjectName ?? null,
    };

    NP_SCHEDULE_HDPE_FIELDS.forEach((fieldName) => {
      payload[fieldName] = getModalValueForSave(fieldName, modalFormData[fieldName]);
    });

    try {
      if (selectedScheduleJob.NpScheduleHasMatch) {
        await dispatch(patchData({
          url: apiUrls.npSchedule,
          data: payload,
          allowFullPayload: true,
          contract: {
            requestCase: "pascal",
            renameMap: {
              projectId: "ProjectId",
            },
          },
        })).unwrap();
      } else {
        const createPayload = { ...payload };
        delete createPayload.ID;

        await dispatch(postData({
          url: apiUrls.npSchedule,
          data: createPayload,
          contract: {
            requestCase: "pascal",
            renameMap: {
              projectId: "ProjectId",
            },
          },
        })).unwrap();
      }

      await reloadNpScheduleRows();
      handleCloseScheduleModal(true);
    } catch (saveError) {
      console.error("Error saving np-schedule record:", saveError);
      setModalError(saveError?.message || "Failed to save np-schedule record");
    } finally {
      setIsSavingModal(false);
    }
  };

  const handleAttachmentFileSelection = (event) => {
    const nextFiles = Array.from(event.target.files || []);
    const currentCount = attachmentItems.length;

    setAttachmentError("");
    setAttachmentMessage("");

    if (currentCount + nextFiles.length > NP_SCHEDULE_ATTACHMENT_MAX_COUNT) {
      setSelectedAttachmentFiles([]);
      setAttachmentError(`A maximum of ${NP_SCHEDULE_ATTACHMENT_MAX_COUNT} attachments is allowed per item.`);
      event.target.value = "";
      return;
    }

    setSelectedAttachmentFiles(nextFiles);
  };

  const handleCloseAttachmentViewer = useCallback(() => {
    setShowAttachmentViewer(false);
    setAttachmentViewerUrl((previousUrl) => {
      if (previousUrl && previousUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previousUrl);
      }

      return "";
    });
    setAttachmentViewerExternalUrl("");
    setAttachmentViewerTitle("");
    setLoadingAttachmentViewer(false);
  }, []);

  const handleCloseAttachmentPicker = useCallback(() => {
    setShowAttachmentPicker(false);
    setAttachmentPickerItems([]);
    setAttachmentPickerJobLabel("");
  }, []);

  const handleOpenAttachmentInViewer = useCallback(async (item, providedAccessToken = null) => {
    const targetUrl = getGraphItemDisplayUrl(item);

    if (!targetUrl) {
      setAttachmentError("Unable to open attachment because no SharePoint URL is available.");
      return;
    }

    setAttachmentError("");
    setAttachmentViewerTitle(item?.name || "Attachment");
    setAttachmentViewerExternalUrl(targetUrl);
    setAttachmentViewerUrl((previousUrl) => {
      if (previousUrl && previousUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previousUrl);
      }

      return "";
    });
    setLoadingAttachmentViewer(true);
    setShowAttachmentViewer(true);

    if (!isViewableAttachmentFile(item?.name)) {
      setLoadingAttachmentViewer(false);
      return;
    }

    try {
      const accessToken = providedAccessToken || await acquireGraphAccessToken();
      const graphContentUrl = `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${item?.id}/content`;

      const response = await axiosInstance.get(graphContentUrl, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const objectUrl = URL.createObjectURL(response.data);
      setAttachmentViewerUrl(objectUrl);
    } catch (previewError) {
      console.error("Failed to load attachment preview:", previewError);
      setAttachmentViewerUrl("");
      setAttachmentError(previewError?.message || "Failed to load attachment preview. Use Open in New Tab as fallback.");
    } finally {
      setLoadingAttachmentViewer(false);
    }
  }, [acquireGraphAccessToken, npSharePointDriveId, npSharePointSiteId]);

  const handleViewRowAttachments = useCallback(async (event, job) => {
    event.stopPropagation();

    const fileIds = parseSharePointFileIds(getNpScheduleAttachmentIdsValue(job));
    if (fileIds.length === 0) {
      return;
    }

    if (!hasNpSharePointConfig) {
      setAttachmentError("NP SharePoint attachment configuration is missing.");
      return;
    }

    const rowId = getProjectId(job) ?? job?.ID;
    setAttachmentActionRowId(rowId);

    try {
      const accessToken = await acquireGraphAccessToken();
      const fetchedItems = await Promise.all(fileIds.map(async (fileId) => {
        try {
          return await fetchGraphItemById(fileId, accessToken);
        } catch (_) {
          return null;
        }
      }));

      const validItems = fetchedItems.filter((item) => item && getGraphItemDisplayUrl(item));
      if (validItems.length === 0) {
        setAttachmentError("Unable to load attachment metadata for this row.");
        return;
      }

      if (validItems.length > 1) {
        setAttachmentPickerItems(validItems);
        setAttachmentPickerJobLabel(
          String(job?.JobNumber ?? "").trim() || String(job?.ProjectName ?? "").trim() || "Selected Row"
        );
        setShowAttachmentPicker(true);
        return;
      }

      const preferredItem = validItems.find((item) => isViewableAttachmentFile(item?.name)) || validItems[0];
      await handleOpenAttachmentInViewer(preferredItem, accessToken);
    } catch (readError) {
      console.error("Failed to load row attachments:", readError);
      setAttachmentError(readError?.message || "Failed to load row attachments.");
    } finally {
      setAttachmentActionRowId(null);
    }
  }, [acquireGraphAccessToken, fetchGraphItemById, handleOpenAttachmentInViewer, hasNpSharePointConfig]);

  const handleSelectAttachmentFromPicker = useCallback(async (item) => {
    handleCloseAttachmentPicker();
    await handleOpenAttachmentInViewer(item);
  }, [handleCloseAttachmentPicker, handleOpenAttachmentInViewer]);

  const handleUploadAttachments = async () => {
    if (!selectedScheduleJob?.NpScheduleRecordID) {
      setAttachmentError("Save the NP Schedule record before adding attachments.");
      return;
    }

    if (!hasNpSharePointConfig) {
      setAttachmentError("NP SharePoint attachment configuration is missing.");
      return;
    }

    if (selectedAttachmentFiles.length === 0) {
      setAttachmentError("Select at least one file to upload.");
      return;
    }

    setIsUploadingAttachments(true);
    setAttachmentError("");
    setAttachmentMessage("");

    try {
      const accessToken = await acquireGraphAccessToken();
      const parentFolderId = await ensureAttachmentFolderId(selectedScheduleJob, accessToken);
      const existingIds = parseSharePointFileIds(getNpScheduleAttachmentIdsValue(selectedScheduleJob));
      const uploadedItems = [];

      for (const file of selectedAttachmentFiles) {
        const storedFileName = `${Date.now()}-${sanitizeAttachmentFileName(file.name)}`;
        const uploadResponse = await axiosInstance.put(
          `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${parentFolderId}:/${encodeGraphPathSegment(storedFileName)}:/content`,
          file,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": file.type || "application/octet-stream",
            },
          }
        );

        uploadedItems.push(uploadResponse.data);
      }

      const nextIds = [...existingIds, ...uploadedItems.map((item) => item?.id).filter(Boolean)];
      await persistAttachmentIds(selectedScheduleJob, nextIds);
      setAttachmentItems((previousItems) => [...previousItems, ...uploadedItems]);
      setSelectedAttachmentFiles([]);
      setAttachmentMessage(`${uploadedItems.length} attachment${uploadedItems.length === 1 ? "" : "s"} uploaded.`);
    } catch (uploadError) {
      console.error("Failed to upload NP attachments:", uploadError);
      setAttachmentError(uploadError?.message || "Failed to upload attachments.");
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  const handleDeleteAttachment = async (itemId) => {
    if (!selectedScheduleJob?.NpScheduleRecordID) {
      setAttachmentError("Save the NP Schedule record before removing attachments.");
      return;
    }

    if (!hasNpSharePointConfig) {
      setAttachmentError("NP SharePoint attachment configuration is missing.");
      return;
    }

    setDeletingAttachmentIds((previousIds) => [...previousIds, itemId]);
    setAttachmentError("");
    setAttachmentMessage("");

    try {
      const accessToken = await acquireGraphAccessToken();

      await axiosInstance.delete(
        `https://graph.microsoft.com/v1.0/sites/${npSharePointSiteId}/drives/${npSharePointDriveId}/items/${itemId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const existingIds = parseSharePointFileIds(getNpScheduleAttachmentIdsValue(selectedScheduleJob));
      const nextIds = existingIds.filter((existingId) => existingId !== itemId);

      await persistAttachmentIds(selectedScheduleJob, nextIds);
      setAttachmentItems((previousItems) => previousItems.filter((item) => item?.id !== itemId));
      setAttachmentMessage("Attachment removed.");
    } catch (deleteError) {
      console.error("Failed to delete NP attachment:", deleteError);
      setAttachmentError(deleteError?.message || "Failed to remove attachment.");
    } finally {
      setDeletingAttachmentIds((previousIds) => previousIds.filter((existingId) => existingId !== itemId));
    }
  };

  const getAttachmentCount = (item) => parseSharePointFileIds(getNpScheduleAttachmentIdsValue(item)).length;

  const renderAttachmentCell = (item) => {
    const rowId = getProjectId(item) ?? item?.ID;
    const attachmentCount = getAttachmentCount(item);
    const isLoadingRowAttachments = attachmentActionRowId === rowId;

    if (attachmentCount <= 0) {
      return <span className="text-muted">-</span>;
    }

    return (
      <Button
        variant="link"
        size="sm"
        className="p-0"
        onClick={(event) => handleViewRowAttachments(event, item)}
        title={`View attachment${attachmentCount === 1 ? "" : "s"} (${attachmentCount})`}
        aria-label={`View attachment${attachmentCount === 1 ? "" : "s"} (${attachmentCount})`}
        disabled={isLoadingRowAttachments || !hasNpSharePointConfig}
      >
        {isLoadingRowAttachments ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <>
            <i className="bi bi-paperclip" />
            <span className="ms-1">{attachmentCount}</span>
          </>
        )}
      </Button>
    );
  };

  const expandedColumns = [
    { key: "JobStatus", label: "Job Status" },
    { key: "ModelNumber", label: "Model Number" },
    { key: "ProjectManager", label: "Project Manager" },
    { key: "MechanicalEngineer", label: "Mechanical Engineer" },
  ];

  const renderCategoryTable = (category, title) => {
    const items = getItemsByCategory(category);
    const filteredAndSortedItems = filterAndSortItems(items, category);

    if (!filteredAndSortedItems || filteredAndSortedItems.length === 0) {
      return null;
    }

    const isEzCategory = category === "ezFertigations";
    const primaryColumns = isEzCategory
      ? ["JobStatus", "PackDate", "WatertronicsFertigation", "FertigationQuantity"]
      : [];
    const visibleHdpeFields = isEzCategory
      ? NP_SCHEDULE_HDPE_TABLE_FIELD_ORDER.filter((fieldName) => fieldName !== "HDPEOutSourced" && fieldName !== "HDPEOrdered")
      : NP_SCHEDULE_HDPE_TABLE_FIELD_ORDER;

    const fullColSpan = 3 + primaryColumns.length + 2 + visibleHdpeFields.length;

    return (
      <div className="mb-4" key={category}>
        <h4 className="mb-3">{title}</h4>
        <div className="table-responsive" style={TABLE_SCROLL_CONTAINER_STYLE}>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th
                  style={getStickyHeaderColumnStyle("expand", {
                    width: `${STICKY_EXPAND_COL_WIDTH}px`,
                    minWidth: `${STICKY_EXPAND_COL_WIDTH}px`,
                    maxWidth: `${STICKY_EXPAND_COL_WIDTH}px`,
                  })}
                ></th>
                <th
                  onClick={() => handleSort(category, "JobNumber")}
                  style={getStickyHeaderColumnStyle("jobNumber", {
                    width: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                    minWidth: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                    maxWidth: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                    cursor: "pointer",
                  })}
                >
                  Job Number
                </th>
                <th
                  onClick={() => handleSort(category, "ProjectName")}
                  style={getStickyHeaderColumnStyle("projectName", {
                    width: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                    minWidth: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                    maxWidth: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                    whiteSpace: "normal",
                    cursor: "pointer",
                  })}
                >
                  Project Name
                </th>
                {isEzCategory && (
                  <>
                    <th onClick={() => handleSort(category, "JobStatus")} style={STICKY_HEADER_STYLE}>Job Status</th>
                    <th onClick={() => handleSort(category, "PackDate")} style={STICKY_HEADER_STYLE}>Pack Date</th>
                    <th onClick={() => handleSort(category, "WatertronicsFertigation")} style={STICKY_HEADER_STYLE}>Watertronics Fertigation</th>
                    <th onClick={() => handleSort(category, "FertigationQuantity")} style={STICKY_HEADER_STYLE}>Fertigation Quantity</th>
                  </>
                )}
                {!isEzCategory && null}
                <th style={STICKY_HEADER_STYLE}>Att</th>
                {visibleHdpeFields.map((fieldName) => (
                  <th
                    key={`${category}-${fieldName}`}
                    onClick={() => handleSort(category, fieldName)}
                    style={{
                      ...STICKY_HEADER_STYLE,
                      ...getHdpeHeaderCellStyle(fieldName),
                      cursor: "pointer",
                    }}
                  >
                    {formatHdpeFieldLabel(fieldName, { includePrefix: false })}
                  </th>
                ))}
                <th style={STICKY_HEADER_STYLE} className="text-center">📅</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedItems.map((item, index) => {
                const rowId = getProjectId(item) || item.ID || `${category}-${index}`;
                const isExpanded = expandedRows.includes(rowId);
                const isEngineeringComplete =
                  item.MechanicalDone &&
                  item.MechanicalDone !== "R" &&
                  String(item.MechanicalDone).trim() !== "";

                return (
                  <React.Fragment key={rowId}>
                    <tr onDoubleClick={() => handleOpenScheduleModal(item, category)}>
                      <td
                        className="text-center align-middle"
                        style={getStickyBodyColumnStyle("expand", {
                          width: `${STICKY_EXPAND_COL_WIDTH}px`,
                          minWidth: `${STICKY_EXPAND_COL_WIDTH}px`,
                          maxWidth: `${STICKY_EXPAND_COL_WIDTH}px`,
                        })}
                      >
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-decoration-none"
                          onClick={() => toggleRowExpansion(rowId)}
                          onDoubleClick={(event) => event.stopPropagation()}
                          aria-label={isExpanded ? "Collapse row" : "Expand row"}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? "v" : ">"}
                        </button>
                      </td>
                      <td
                        onClick={canOpenPortalJob(item) ? () => handleJobNumberClick(item) : undefined}
                        onDoubleClick={(event) => handleJobNumberDoubleClick(event, item)}
                        style={{
                          ...getStickyBodyColumnStyle("jobNumber", {
                            width: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                            minWidth: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                            maxWidth: `${STICKY_JOB_NUMBER_COL_WIDTH}px`,
                          }),
                          cursor: "pointer",
                        }}
                        className="align-middle"
                      >
                        {isEngineeringComplete && (
                          <span
                            className="me-2"
                            style={{ color: "green" }}
                            title="Engineering Complete"
                            aria-label="Engineering Complete"
                          >
                            ✓
                          </span>
                        )}
                        {item.JobNumber || "N/A"}
                      </td>
                      <td
                        className="align-middle"
                        style={getStickyBodyColumnStyle("projectName", {
                          width: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                          minWidth: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                          maxWidth: `${STICKY_PROJECT_NAME_COL_WIDTH}px`,
                          whiteSpace: "normal",
                        })}
                      >
                        {item.ProjectName || "N/A"}
                      </td>
                      {isEzCategory && (
                        <>
                          <td>{item.JobStatus || "N/A"}</td>
                          <td>{formatDate(item.PackDate)}</td>
                          <td>{item.WatertronicsFertigation || "N/A"}</td>
                          <td>{item.FertigationQuantity || "N/A"}</td>
                        </>
                      )}
                      {!isEzCategory && null}
                      <td className="text-center">{renderAttachmentCell(item)}</td>
                      {visibleHdpeFields.map((fieldName) => (
                        <td
                          key={`${category}-${rowId}-${fieldName}`}
                          style={{
                            ...getHdpeBoundaryStyle(fieldName),
                            ...(fieldName === "HDPEDetails" ? { minWidth: "280px", whiteSpace: "normal" } : {}),
                          }}
                        >
                          {renderCellValue(item, fieldName, category)}
                        </td>
                      ))}
                      <td className="text-center">
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => navigate(PRODUCTION_SCHEDULE_PATH)}
                          title="Go to Production Schedule"
                        >
                          📅
                        </Button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td></td>
                        <td colSpan={fullColSpan - 1} className="p-0">
                          <div className="table-responsive">
                            <Table bordered size="sm" className="mb-0">
                              <thead>
                                <tr>
                                  {expandedColumns.map((column) => (
                                    <th key={column.key}>{column.label}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  {expandedColumns.map((column) => (
                                    <td key={`${rowId}-${column.key}`}>{item[column.key] || "N/A"}</td>
                                  ))}
                                </tr>
                              </tbody>
                            </Table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  if (!canView("user.np-jobs")) {
    return null;
  }

  return (
    <div className="m-4">
      <div className="mb-3 text-center">
        <h1>HDPE</h1>
      </div>

      <Row className="mb-3">
        <Col lg="auto">
          <Form.Control
            type="text"
            placeholder="Search by Job Number or Project Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md="auto">
          <Form.Check
            type="checkbox"
            label="Hide Shipped/canceled Jobs"
            name="hideShippedJobs"
            checked={filters.hideShippedJobs}
            onChange={handleFilterChange}
          />
        </Col>
      </Row>

    

      {error && (
        <Alert variant="danger" className="mb-3">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}

      {npScheduleError && (
        <Alert variant="warning" className="mb-3 py-2">
          Could not load np-schedule records. HDPE and attachment columns may be incomplete.
        </Alert>
      )}

      {attachmentError && !showScheduleModal && (
        <Alert variant="danger" className="mb-3 py-2">
          {attachmentError}
        </Alert>
      )}

      {!hasNpSharePointConfig && (
        <Alert variant="warning" className="mb-3 py-2">
          Attachment features are disabled. Set REACT_APP_SHAREPOINT_NP_SITE_ID and REACT_APP_SHAREPOINT_NP_DRIVE_ID to enable them.
        </Alert>
      )}

      {Object.keys(categorizedData).length > 0 && (
        <div>
          {categories.map(({ key, title }) => renderCategoryTable(key, title))}
        </div>
      )}

      {!loading && Object.keys(categorizedData).length === 0 && (
        <Alert variant="info">
          <strong>No Production Items Found</strong>
          <p className="mb-0">No items matching the NP Jobs criteria were found in the current data.</p>
        </Alert>
      )}

      <Modal
        show={showScheduleModal}
        onHide={() => handleCloseScheduleModal()}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            NP Schedule Editor: {selectedScheduleJob?.JobNumber || ""}
            {selectedScheduleJob?.ProjectName ? ` - ${selectedScheduleJob.ProjectName}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && (
            <Alert variant="danger" className="py-2">
              {modalError}
            </Alert>
          )}

          <div className="mb-4">
            <h6 className="border-bottom pb-2 mb-3">Job Details</h6>
            <Row className="g-3 mb-1">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Job Number</Form.Label>
                  <Form.Control type="text" value={selectedScheduleJob?.JobNumber || ""} disabled readOnly />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Project Name</Form.Label>
                  <Form.Control type="text" value={selectedScheduleJob?.ProjectName || ""} disabled readOnly />
                </Form.Group>
              </Col>
            </Row>
            <Row className="g-3 mb-1">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Project Manager</Form.Label>
                  <Form.Control type="text" value={selectedScheduleJob?.ProjectManager || ""} disabled readOnly />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Mechanical Engineer</Form.Label>
                  <Form.Control type="text" value={selectedScheduleJob?.MechanicalEngineer || ""} disabled readOnly />
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="mb-4" style={{
            backgroundColor: SECTION_THEME.hdpe.cellBg,
            border: `1px solid ${SECTION_THEME.hdpe.border}`,
            borderLeft: `5px solid ${SECTION_THEME.hdpe.border}`,
            borderRadius: "0.75rem",
            padding: "1rem",
          }}>
            <h6 style={{
              color: SECTION_THEME.hdpe.accent,
              borderBottom: `1px solid ${SECTION_THEME.hdpe.border}`,
              paddingBottom: "0.5rem",
              marginBottom: "1rem",
              fontWeight: 600,
            }}>
              HDPE Schedule
            </h6>
            <Row className="g-3">
              {NP_SCHEDULE_HDPE_FIELDS.map((fieldName) => (
                <Col
                  key={fieldName}
                  md={fieldName === "HDPEDetails" ? 12 : 6}
                  lg={fieldName === "HDPEDetails" ? 12 : 4}
                >
                  <Form.Group controlId={`np-schedule-${fieldName}`}>
                    <Form.Label>{formatHdpeFieldLabel(fieldName)}</Form.Label>
                    {fieldName === "HDPEDetails" ? (
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={modalFormData[fieldName] ?? ""}
                        onChange={(event) => handleModalFieldChange(fieldName, event.target.value)}
                        placeholder="Enter details"
                      />
                    ) : fieldName === "HDPEDueDate" ? (
                      <Form.Control
                        type="date"
                        value={modalFormData[fieldName] ?? ""}
                        onChange={(event) => handleModalFieldChange(fieldName, event.target.value)}
                      />
                    ) : NP_HDPE_STATUS_FIELDS.has(fieldName) ? (
                      <Form.Select
                        value={modalFormData[fieldName] ?? ""}
                        onChange={(event) => handleModalFieldChange(fieldName, event.target.value)}
                      >
                        <option value="">Select...</option>
                        {NP_SCHEDULE_STATUS_OPTIONS.map((optionLabel) => (
                          <option key={`${fieldName}-${optionLabel}`} value={optionLabel}>{optionLabel}</option>
                        ))}
                      </Form.Select>
                    ) : NP_HDPE_BOOLEAN_FIELDS.has(fieldName) ? (
                      <div>
                        <Form.Check
                          type="switch"
                          id={`np-schedule-toggle-${fieldName}`}
                          checked={(modalFormData[fieldName] ?? "") === "true"}
                          onChange={(event) => handleModalFieldChange(fieldName, event.target.checked ? "true" : "false")}
                          label={getBooleanToggleDisplayLabel(modalFormData[fieldName] ?? "")}
                        />
                        {(modalFormData[fieldName] ?? "") === "" && (
                          <Form.Text className="text-muted">Currently not set. Toggling will save Yes or No.</Form.Text>
                        )}
                      </div>
                    ) : (
                      <Form.Control
                        type="text"
                        value={modalFormData[fieldName] ?? ""}
                        onChange={(event) => handleModalFieldChange(fieldName, event.target.value)}
                      />
                    )}
                  </Form.Group>
                </Col>
              ))}
            </Row>
          </div>

          <div className="mb-2">
            <h6 className="border-bottom pb-2 mb-3">Attachments</h6>

            {!hasNpSharePointConfig && (
              <Alert variant="warning" className="py-2">
                NP SharePoint attachment configuration is missing. Set REACT_APP_SHAREPOINT_NP_SITE_ID and REACT_APP_SHAREPOINT_NP_DRIVE_ID to enable attachments.
              </Alert>
            )}

            {!selectedScheduleJob?.NpScheduleRecordID && hasNpSharePointConfig && (
              <Alert variant="info" className="py-2">
                Save the NP Schedule record once before adding attachments.
              </Alert>
            )}

            {attachmentError && (
              <Alert variant="danger" className="py-2">
                {attachmentError}
              </Alert>
            )}

            {attachmentMessage && (
              <Alert variant="success" className="py-2">
                {attachmentMessage}
              </Alert>
            )}

            <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
              <Form.Group controlId="np-schedule-attachments" className="flex-grow-1 mb-0">
                <Form.Label>Add attachments</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={handleAttachmentFileSelection}
                  disabled={!hasNpSharePointConfig || !selectedScheduleJob?.NpScheduleRecordID || isUploadingAttachments}
                />
                <Form.Text className="text-muted">
                  Up to {NP_SCHEDULE_ATTACHMENT_MAX_COUNT} attachments per item. Current count: {attachmentItems.length}.
                </Form.Text>
              </Form.Group>
              <Button
                variant="outline-primary"
                onClick={handleUploadAttachments}
                disabled={
                  !hasNpSharePointConfig ||
                  !selectedScheduleJob?.NpScheduleRecordID ||
                  isUploadingAttachments ||
                  selectedAttachmentFiles.length === 0
                }
              >
                {isUploadingAttachments ? "Uploading..." : `Upload ${selectedAttachmentFiles.length > 0 ? `(${selectedAttachmentFiles.length})` : ""}`}
              </Button>
            </div>

            {loadingAttachments ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : attachmentItems.length > 0 ? (
              <Table bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Modified</th>
                    <th>Size</th>
                    <th style={{ width: "180px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {attachmentItems.map((item) => {
                    const isDeleting = deletingAttachmentIds.includes(item?.id);

                    return (
                      <tr key={item?.id || item?.name}>
                        <td>{item?.name || "Unknown file"}</td>
                        <td>{item?.lastModifiedDateTime ? new Date(item.lastModifiedDateTime).toLocaleDateString() : "-"}</td>
                        <td>{formatAttachmentFileSize(item?.size)}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleOpenAttachmentInViewer(item)}
                              disabled={!getGraphItemDisplayUrl(item)}
                            >
                              View
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteAttachment(item?.id)}
                              disabled={isDeleting || !item?.id}
                            >
                              {isDeleting ? "Removing..." : "Delete"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <div className="text-muted">No attachments.</div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => handleCloseScheduleModal()}
            disabled={isSavingModal}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveScheduleModal}
            disabled={isSavingModal || !selectedScheduleJob}
          >
            {isSavingModal ? "Saving..." : "Save NP Schedule"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAttachmentPicker}
        onHide={handleCloseAttachmentPicker}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Select Attachment{attachmentPickerJobLabel ? ` - ${attachmentPickerJobLabel}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <Table bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Name</th>
                <th style={{ width: "120px" }}>Size</th>
                <th style={{ width: "90px" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {attachmentPickerItems.map((item) => (
                <tr key={item?.id || item?.name}>
                  <td>{item?.name || "Unknown file"}</td>
                  <td>{formatAttachmentFileSize(item?.size)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleSelectAttachmentFromPicker(item)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAttachmentPicker}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <PdfViewerModal
        show={showAttachmentViewer}
        onHide={handleCloseAttachmentViewer}
        title={attachmentViewerTitle || "Attachment Viewer"}
        loading={loadingAttachmentViewer}
        viewerUrl={attachmentViewerUrl}
        externalUrl={attachmentViewerExternalUrl}
        dialogClassName="engineering-pdf-viewer-modal"
      />
    </div>
  );
};

export default NPJobs;
